import React from 'react'

import * as c from '../../../common'
import {
  ROOF_AUTH_FIELDS,
  RoofAuthField,
  authorizationSigned,
  roofAuthorizationValues,
  roofAuthorizationSignaturePatch,
} from '../../../common/helpers/roofThirdPartyAuthorization'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import Modal from '../Modal'
import PageReport from '../PageReport'
import SignatureControlled from '../SignatureControlled'
import styles from './RoofThirdPartyAuthorization.module.css'

export interface RoofThirdPartyAuthorizationProps {
  company: string
  customerID: string
  customer: c.Customer
  noPDF?: boolean
  readOnly?: boolean
  isPDF?: boolean
}

/**
 * Insurance Third Party Authorization Form — Jaron's Oct 29 PDF (ticket
 * 13411499936): title, 5-row table, then the authorization paragraph with
 * "(Company Name)" filled in. Independent consent: own fresh drawing, never
 * adopted or required by Finish; fields freeze once signed (V99).
 */
export default React.memo<RoofThirdPartyAuthorizationProps>(
  function RoofThirdPartyAuthorization({
    company,
    customerID,
    customer,
    noPDF,
    readOnly = false,
    isPDF = false,
  }) {
    const [open, setOpen] = React.useState(false)
    const [cleared, setCleared] = React.useState<ReadonlySet<string>>(
      () => new Set(),
    )
    const { contractCompanyFull, shouldOfferCompanySelector } =
      r.useContractCompany(customerID)
    // Same contractor name the Roof Claim agreement pages print.
    const defaults = {
      company: shouldOfferCompanySelector
        ? contractCompanyFull.longName
        : c.RoofClaimingContractorToLabel.ecohome,
    }
    const values = roofAuthorizationValues(customer, defaults, cleared)
    const signed = authorizationSigned(customer)
    const disabled = readOnly || signed
    const sign = (signature: string) => {
      if (isPDF || signed) return
      const patch = roofAuthorizationSignaturePatch(
        customer,
        signature,
        Date.now(),
        defaults,
        cleared,
      )
      if (patch) c.updateCustomer(customerID, patch)
    }
    const onEdit = (field: RoofAuthField, value: string) =>
      setCleared((prev) => {
        if (value === '' ? prev.has(field) : !prev.has(field)) return prev
        const next = new Set(prev)
        if (value === '') next.add(field)
        else next.delete(field)
        return next
      })
    const field = (
      key: keyof typeof ROOF_AUTH_FIELDS,
      label: string,
      inline = false,
    ) => {
      const name = ROOF_AUTH_FIELDS[key]
      return (
        <AuthorizationField
          customerID={customerID}
          field={name}
          label={label}
          value={values[name]}
          disabled={disabled}
          isPDF={isPDF}
          inline={inline}
          onEdit={onEdit}
        />
      )
    }
    const row = (label: string, cell: React.ReactNode) => (
      <tr>
        <th scope="row">{label}</th>
        <td>{cell}</td>
      </tr>
    )
    return (
      <>
        <PageReport
          company={company}
          companyBorder={false}
          autoHeight
          noPDF={noPDF}
        >
          <div className={styles['container']} data-roof-authorization>
            <h2 className={styles['title']}>
              Insurance Third Party Authorization Form
            </h2>
            <table className={styles['table']}>
              <tbody>
                {row('Insured Name', field('policyholder', 'Insured Name'))}
                {row(
                  'Insured Property Address',
                  field('address', 'Insured Property Address'),
                )}
                {row('Claim Number', field('claimNumber', 'Claim Number'))}
                {row('Policy Number', field('policyNumber', 'Policy Number'))}
                <tr className={styles['signature-row']}>
                  <th scope="row">
                    <strong>Policyholder Signature:</strong>
                  </th>
                  <td>
                    {signed ? (
                      <div className={styles['signature']}>
                        <img
                          alt="Policyholder authorization signature"
                          src={customer.roof_third_party_sign}
                        />
                        {!!customer.roof_third_party_sign_date && (
                          <span className={styles['date']}>
                            Date:{' '}
                            {c.formatContractDate(
                              customer.roof_third_party_sign_date,
                              company,
                              'MM/DD/YYYY - hh:mma',
                            )}
                          </span>
                        )}
                      </div>
                    ) : (
                      !isPDF && (
                        <Button
                          data-guided-signature="roof_third_party_sign"
                          onClick={() => setOpen(true)}
                          size="small"
                          sx={{
                            backgroundColor: c.companyColors[company],
                            '&:hover': {
                              backgroundColor: c.companyColors[company],
                            },
                          }}
                          variant="contained"
                        >
                          Sign third-party authorization
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className={styles['text']}>
              *I, the undersigned policyholder, hereby authorize{' '}
              {field('company', 'Company Name', true)} to act on my behalf
              regarding the above-referenced insurance claim. This includes, but
              is not limited to: Communicating with my insurance company,
              adjusters, and mortgage company. Submitting documentation,
              estimates, invoices, and photographs related to the claim.
              Requesting and receiving information pertaining to my claim. This
              authorization does not assign policy rights or benefits; it only
              provides permission for the authorized third party to assist in
              managing claim-related communications and documentation.
            </p>
          </div>
        </PageReport>
        {!isPDF && (
          <Modal
            open={open && !isPDF && !signed}
            onRequestClose={() => setOpen(false)}
          >
            <div aria-label="Signature pad" className={styles['dialog']}>
              <div className={styles['dialog-header']}>
                <p className={styles['eyebrow']}>Authorization</p>
                <p className={styles['dialog-title']}>
                  Insurance Third Party Authorization
                </p>
                <p className={styles['dialog-text']}>
                  Separate from the Roof Claim agreement. Your signature goes
                  only on this form.
                </p>
              </div>
              <SignatureControlled
                onRequestClose={() => setOpen(false)}
                onSign={sign}
                submitLabel="Sign authorization"
                variant="card"
              />
            </div>
          </Modal>
        )}
      </>
    )
  },
)
function AuthorizationField({
  customerID,
  field,
  label,
  value,
  disabled,
  isPDF,
  inline,
  onEdit,
}: {
  customerID: string
  field: RoofAuthField
  label: string
  value: string
  disabled: boolean
  isPDF: boolean
  inline: boolean
  onEdit(field: RoofAuthField, value: string): void
}) {
  const [local, setValue] = r.useCustomerField(customerID, field)
  // Once typed in, show what was typed: the store only hears of a save through
  // the batched echo, and reading it here dropped keys ("QA-1" saved as "1").
  const [touched, setTouched] = React.useState(false)
  const shown = touched && !disabled ? String(local ?? '') : value
  // The form prints "(Company Name)" where the contractor goes.
  const placeholder = inline ? `(${label})` : undefined
  if (isPDF)
    return (
      <span className={inline ? styles['inline-value'] : styles['value']}>
        {shown || placeholder}
      </span>
    )
  const input = (
    <input
      aria-label={label}
      className={inline ? styles['inline-input'] : styles['input']}
      disabled={disabled}
      placeholder={placeholder}
      size={1}
      value={shown}
      onChange={(event) => {
        if (disabled) return
        setTouched(true)
        onEdit(field, event.target.value)
        setValue(event.target.value)
      }}
    />
  )
  // Inline: a hidden copy of the text sizes the box, so the whole name shows.
  return inline ? (
    <span className={styles['autosize']} data-value={shown || placeholder}>
      {input}
    </span>
  ) : (
    input
  )
}
